import { Group, Pagination, Table, Tabs } from "@mantine/core";

import type {
  CptFrequencyReportResponse,
  CptFrequencyRow,
} from "../../services/reports.service";
import { AccountPhysicianCptTable } from "./AccountPhysicianCptTable";
import { CodeTable } from "./CodeTable";
import { PhysicianRow } from "./PhysicianRow";
import { PracticeCodeTable } from "./PracticeCodeTable";
import { PracticeRow } from "./PracticeRow";
import { PrefixCodeTable } from "./PrefixCodeTable";
import { ReportEmptyState } from "./ReportEmptyState";
import { SurgeryCenterRow } from "./SurgeryCenterRow";

interface CptFrequencyTabsProps {
  data?: CptFrequencyReportResponse;
  byCodeRows: CptFrequencyRow[];
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export function CptFrequencyTabs({
  data,
  byCodeRows,
  page,
  totalPages,
  onPageChange,
}: CptFrequencyTabsProps) {
  return (
    <Tabs defaultValue="byCode">
      <Tabs.List>
        <Tabs.Tab value="byCode">By Code</Tabs.Tab>
        <Tabs.Tab value="byPractice">By Practice</Tabs.Tab>
        <Tabs.Tab value="byPracticeCode">By Practice + Code</Tabs.Tab>
        <Tabs.Tab value="byPrefix">By Prefix</Tabs.Tab>
        <Tabs.Tab value="byAccountPhysician">By Account + Physician</Tabs.Tab>
        <Tabs.Tab value="byReferringPhysician">By Referring Physician</Tabs.Tab>
        <Tabs.Tab value="bySurgeryCenter">By Surgery Center</Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="byCode" pt="md">
        {!data || byCodeRows.length === 0 ? (
          <ReportEmptyState message="No CPT data for selected period." />
        ) : (
          <>
            <CodeTable rows={byCodeRows} />
            {totalPages > 1 && (
              <Group justify="center" mt="md">
                <Pagination
                  value={page}
                  onChange={onPageChange}
                  total={totalPages}
                />
              </Group>
            )}
          </>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="byPractice" pt="md">
        {!data || data.byPractice.length === 0 ? (
          <ReportEmptyState message="No practice data for selected period." />
        ) : (
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={32} />
                <Table.Th>Practice</Table.Th>
                <Table.Th>Total CPT Count</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.byPractice.map((practice) => (
                <PracticeRow key={practice.practiceId} practice={practice} />
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="byPracticeCode" pt="md">
        {!data || data.byPractice.length === 0 ? (
          <ReportEmptyState message="No data for selected period." />
        ) : (
          <PracticeCodeTable byPracticeRows={data.byPractice} />
        )}
      </Tabs.Panel>

      <Tabs.Panel value="byPrefix" pt="md">
        {!data || data.byPrefix.length === 0 ? (
          <ReportEmptyState message="No prefix data for selected period." />
        ) : (
          <PrefixCodeTable rows={data.byPrefix} />
        )}
      </Tabs.Panel>

      <Tabs.Panel value="byAccountPhysician" pt="md">
        {!data || data.byAccountPhysician.length === 0 ? (
          <ReportEmptyState message="No account data for selected period." />
        ) : (
          <AccountPhysicianCptTable rows={data.byAccountPhysician} />
        )}
      </Tabs.Panel>

      <Tabs.Panel value="byReferringPhysician" pt="md">
        {!data || data.byPhysician.length === 0 ? (
          <ReportEmptyState message="No physician data for selected period." />
        ) : (
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={32} />
                <Table.Th>Referring Physician</Table.Th>
                <Table.Th>Total CPT Count</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.byPhysician.map((physician) => (
                <PhysicianRow
                  key={physician.physicianId}
                  physician={physician}
                />
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="bySurgeryCenter" pt="md">
        {!data || data.bySurgeryCenter.length === 0 ? (
          <ReportEmptyState message="No surgery center data for selected period." />
        ) : (
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={32} />
                <Table.Th>Surgery Center</Table.Th>
                <Table.Th>Total CPT Count</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.bySurgeryCenter.map((surgeryCenter) => (
                <SurgeryCenterRow
                  key={surgeryCenter.surgeryCenterLabId}
                  surgeryCenter={surgeryCenter}
                />
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Tabs.Panel>
    </Tabs>
  );
}
